import { HttpHeaders, HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AttributeModel } from "../main/dynamic/model/attribute.model";
import { SearchParamsModel } from "../model/search-params.model";
import { AuthenticationService } from "./auth.service";

export abstract class ResourceService<T> {

    protected resourceUrl: string

    constructor(
        protected http: HttpClient,
        private resourceName: string,
        private apiUrl: string,
        private authService: AuthenticationService
    ) {
        this.resourceUrl = `${this.apiUrl}${this.resourceName ? `/${this.resourceName }`: ''}`
    }

    get headers() {
        return new HttpHeaders()
            .set("Content-Type", "application/json")
            .set("Accept", "application/json")
            .set("Authorization", `Bearer ${this.authService.getToken()}`)
    }

    get() : Observable<Array<T>> {
        return this.http
            .get<Array<T>>(this.resourceUrl, {
                headers : this.headers
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    getWithComplement<T>(complement: string, customBaseUrl?: string): Observable<T> {
        const baseUrl = customBaseUrl || this.resourceUrl;
        const url = complement.startsWith('?') || complement.startsWith('&')
            ? `${baseUrl}${complement}`
            : `${baseUrl}/${complement}`;
        
        return this.http
            .get<T>(url, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            );
    }    

   
    getBy(resourceId: string) : Observable<T> {
        return this.http
            .get<T>(`${this.resourceUrl}/${encodeURIComponent(String(resourceId))}`, {
                headers : this.headers
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    create(resource: T) : Observable<T> {
        if (resource == null) {
            throw new Error('Recurso não informado.');
        }

        return this.http
            .post<T>(`${this.resourceUrl}`, resource, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    patch(id: string, body: any) : Observable<any> {
        return this.http
            .patch<any>(`${this.resourceUrl}/${id}`, body, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    patchWithComplement(complement: string, body: any) : Observable<any> {
        return this.http
            .patch<any>(`${this.resourceUrl}/${complement}`, body, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    postFormData<TReturn>(route: string, data: FormData) : Observable<TReturn> {
        if (data == null) {
            throw new Error('Data missing.');
        }

        return this.http
            .post<TReturn>(`${this.resourceUrl}/${route}`, data, { 
                headers : new HttpHeaders()
                    .set("Authorization", `Bearer ${this.authService.getToken()}`)
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    postGeneric<TReturn, TInput>(resource: TInput) : Observable<TReturn> {
        if (resource == null) {
            throw new Error('Recurso não informado.');
        }

        return this.http
            .post<TReturn>(`${this.resourceUrl}`, resource, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    postWithComplement<TReturn, TInput>(complement: string, body:TInput): Observable<TReturn> {
        return this.http
            .post<TReturn>(`${this.resourceUrl}/${complement}`, body, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    replace(resource: T, resourceId: string) : Observable<T> {
        return this.http
            .put<T>(`${this.resourceUrl}/${encodeURIComponent(String(resourceId))}`, resource, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    update(resource: any, resourceId: string) : Observable<any> {
        if (resource == null) {
            throw new Error('Recurso não informado.');
        }

        return this.http
            .patch<any>(`${this.resourceUrl}/${encodeURIComponent(String(resourceId))}`, resource, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    delete(resourceId: string) : Observable<T> {
        return this.http
            .delete<T>(`${this.resourceUrl}/${encodeURIComponent(String(resourceId))}`, { 
                headers : this.headers 
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    deleteWithComplement<T>(complement: string, resourceId: string): Observable<T> {
        return this.http
            .delete<T>(`${this.resourceUrl}/${complement}/${encodeURIComponent(String(resourceId))}`, {
                headers: this.headers
            })
            .pipe(
                catchError(this.handleError)
            )
    }

    protected getFormattedParams(name: string, params: string[]): string {
        let result = ""

        if(!params || params.length == 0) {
            return result
        }

        for (const id of params) {
            result += `${result.length > 0 ? "&" : ""}${name}=${id}`
        }

        return result
    }

    protected handleError(errorResponse: HttpErrorResponse) {
        if(errorResponse.status >= 400  && 
            errorResponse.status < 500 && 
            errorResponse.error && errorResponse.error.error) {

            if(!errorResponse.error.error) {
                return throwError({
                    status: errorResponse.status,
                    message: "Algo errado aconteceu; Por favor, entre em contato com o suporte."
                })
            }

            return throwError({
                status: errorResponse.status,
                message: errorResponse.error.error
            })
        } else {
            if (errorResponse.error instanceof ErrorEvent) {
                console.error('Um erro ocorreu:', errorResponse.error.error)
            } else {
                console.error(
                `código de retorno: ${errorResponse.status}, ` +
                `corpo: ${errorResponse.error}`)
            }
            
            return throwError({
                status: errorResponse.status,
                message: "Algo errado aconteceu; Por favor, tente novamente."
            })
        }
    }

    protected buildAttributeQueryParams(attributes: AttributeModel[], params: HttpParams): HttpParams {
        let key = 0
        if(attributes) {
            for (let i = 0; i < attributes.length; i++) {
                const attr = attributes[i];
                
                if(null != attr.value && attr.value != "") {
                    params = params.set(`attributes[${key}].key`, attr.key);
                    params = params.set(`attributes[${key}].value`, attr.value);
                    params = params.set(`attributes[${key}].type`, attr.type);

                    for (let j = 0; j < attr.options.length; j++) {
                        const opt = attr.options[j];
    
                        params = params.set(`attributes[${key}].options[${j}].key`, opt.key);
                        params = params.set(`attributes[${key}].options[${j}].value`, opt.value);
                    }
                    key++
                }
            }
        }

        return params
    }

    protected BuildProcessSearchParams(pageNumber: number, pageSize: number, options: { searchParams?: SearchParamsModel; }) {
        let params = new HttpParams()
            .set("PageNumber", String(pageNumber))
            .set("PageSize", String(pageSize));

        if (options && options.searchParams) {
            Object.keys(options.searchParams).forEach(function (key) {
                if (key != "processesIds" && key != "filter" && key != "roles" && key != "attributes" &&
                    options.searchParams[key] &&
                    options.searchParams[key] != "") {
                    params = params.set(key, options.searchParams[key]);
                }
            });

            if (options.searchParams.filter) {
                options.searchParams.filter.forEach(function (value) {
                    params = params.append("filter", value);
                });
            }

            params = this.buildAttributeQueryParams(this.getFilterAttributes(options.searchParams), params);
        }
        return params;
    }

    getFilterAttributes(searchParams: SearchParamsModel){
        return searchParams.attributes?.flatMap(a =>a.attributes.filter(f => f.key == 'filterPath'))?.flatMap(o => o.attributes)
    }
}